import { action, makeObservable, observable, runInAction } from "mobx";
import CampaignAPI from "../api/CampaignAPI";
import {
  CampaignListItem,
  CampaignEligibleEmployeesResponse,
  CampaignEligibleEmployeeRow,
} from "../../../models/Campaigns/Campaign";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import { ShipmentsDetails } from "../../../models/Shipments";

export type CampaignStatusTab = "active" | "paused" | "expired";
export type CampaignDetailsTab = "eligible_employees" | "today_shipments";

export class CampaignStore {
  campaignAPI: CampaignAPI;
  rows: CampaignListItem[];
  isLoading: boolean;
  errorMessage: string | null;
  pageNo: number;
  limit: number;
  totalCount: number;
  isFiltersExpanded: boolean;
  statusTab: CampaignStatusTab;
  searchInput: string;
  appliedSearch: string;

  campaignDetailsTab: CampaignDetailsTab;

  eligibleEmployees: CampaignEligibleEmployeeRow[];
  isEligibleEmployeesLoading: boolean;
  eligibleEmployeesErrorMessage: string | null;
  eligibleEmployeesSearchInput: string;
  eligibleEmployeesAppliedSearch: string;
  campaignDetailsCustomerId: number | null;

  todayShipments: ShipmentsDetails[];
  isTodayShipmentsLoading: boolean;
  todayShipmentsErrorMessage: string | null;
  todayShipmentsSearchInput: string;
  todayShipmentsAppliedSearch: string;
  todayShipmentsPageNo: number;
  todayShipmentsLimit: number;
  todayShipmentsTotalCount: number;
  todayShipmentsTotalPages: number;

  constructor() {
    this.campaignAPI = new CampaignAPI();
    this.rows = [];
    this.isLoading = false;
    this.errorMessage = null;
    this.pageNo = 1;
    this.limit = 10;
    this.totalCount = 0;
    this.isFiltersExpanded = true;
    this.statusTab = "active";
    this.searchInput = "";
    this.appliedSearch = "";

    this.campaignDetailsTab = "eligible_employees";

    this.eligibleEmployees = [];
    this.isEligibleEmployeesLoading = false;
    this.eligibleEmployeesErrorMessage = null;
    this.eligibleEmployeesSearchInput = "";
    this.eligibleEmployeesAppliedSearch = "";
    this.campaignDetailsCustomerId = null;

    this.todayShipments = [];
    this.isTodayShipmentsLoading = false;
    this.todayShipmentsErrorMessage = null;
    this.todayShipmentsSearchInput = "";
    this.todayShipmentsAppliedSearch = "";
    this.todayShipmentsPageNo = 1;
    this.todayShipmentsLimit = 30;
    this.todayShipmentsTotalCount = 0;
    this.todayShipmentsTotalPages = 0;

    makeObservable(this, {
      rows: observable,
      isLoading: observable,
      errorMessage: observable,
      pageNo: observable,
      limit: observable,
      totalCount: observable,
      isFiltersExpanded: observable,
      statusTab: observable,
      searchInput: observable,
      appliedSearch: observable,

      campaignDetailsTab: observable,
      eligibleEmployees: observable,
      isEligibleEmployeesLoading: observable,
      eligibleEmployeesErrorMessage: observable,
      eligibleEmployeesSearchInput: observable,
      eligibleEmployeesAppliedSearch: observable,
      campaignDetailsCustomerId: observable,

      todayShipments: observable,
      isTodayShipmentsLoading: observable,
      todayShipmentsErrorMessage: observable,
      todayShipmentsSearchInput: observable,
      todayShipmentsAppliedSearch: observable,
      todayShipmentsPageNo: observable,
      todayShipmentsLimit: observable,
      todayShipmentsTotalCount: observable,
      todayShipmentsTotalPages: observable,

      setCampaignDetailsTab: action,
      setEligibleEmployeesSearchInput: action,
      applyEligibleEmployeesFilters: action,
      setTodayShipmentsSearchInput: action,
      applyTodayShipmentsFilters: action,
      setTodayShipmentsPageNo: action,
      setTodayShipmentsLimit: action,
      getCampaignEligibleEmployees: action,
      getCampaignTodayShipments: action,
      clearCampaignDetailsState: action,

      setStatusTab: action,
      setSearchInput: action,
      toggleFilters: action,
      setPageNo: action,
      setLimit: action,
      initializeFromQuery: action,
      applyFilters: action,
      getCampaignListing: action,
      reset: action,
    });
  }

  setCampaignDetailsTab = (tab: CampaignDetailsTab) => {
    this.campaignDetailsTab = tab;
  };

  setEligibleEmployeesSearchInput = (value: string) => {
    this.eligibleEmployeesSearchInput = value;
  };

  applyEligibleEmployeesFilters = () => {
    this.eligibleEmployeesAppliedSearch = this.eligibleEmployeesSearchInput.trim();
  };

  setTodayShipmentsSearchInput = (value: string) => {
    this.todayShipmentsSearchInput = value;
  };

  applyTodayShipmentsFilters = () => {
    this.todayShipmentsAppliedSearch = this.todayShipmentsSearchInput.trim();
    this.todayShipmentsPageNo = 1;
  };

  setTodayShipmentsPageNo = (pageNo: number) => {
    this.todayShipmentsPageNo = pageNo;
  };

  setTodayShipmentsLimit = (limit: number) => {
    this.todayShipmentsLimit = limit;
    this.todayShipmentsPageNo = 1;
  };

  setSearchInput = (value: string) => {
    this.searchInput = value;
  };

  setStatusTab = (tab: CampaignStatusTab) => {
    this.statusTab = tab;
    this.pageNo = 1;
  };

  toggleFilters = () => {
    this.isFiltersExpanded = !this.isFiltersExpanded;
  };

  setPageNo = (pageNo: number) => {
    this.pageNo = pageNo;
  };

  setLimit = (limit: number) => {
    this.limit = limit;
    this.pageNo = 1;
  };

  initializeFromQuery = (
    search?: string,
    pageNo?: number,
    limit?: number,
    statusTab?: string,
  ) => {
    const parsedPageNo = Number(pageNo);
    const parsedLimit = Number(limit);
    const normalizedStatusTab: CampaignStatusTab =
      statusTab === "active" || statusTab === "paused" || statusTab === "expired"
        ? statusTab
        : "active";

    this.searchInput = search?.trim() || "";
    this.appliedSearch = search?.trim() || "";
    this.pageNo =
      Number.isFinite(parsedPageNo) && parsedPageNo > 0 ? parsedPageNo : 1;
    this.limit =
      Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : 10;
    this.statusTab = normalizedStatusTab;
  };

  applyFilters = () => {
    this.appliedSearch = this.searchInput.trim();
    this.pageNo = 1;
  };

  getCampaignListing = async () => {
    const trimmedSearch = this.appliedSearch.trim();
    const parsedCampaignId = Number(trimmedSearch);
    const campaignId =
      trimmedSearch.length > 0 &&
      Number.isFinite(parsedCampaignId) &&
      parsedCampaignId > 0
        ? parsedCampaignId
        : undefined;
    const searchText = campaignId ? undefined : trimmedSearch || undefined;
    const status = this.statusTab;

    runInAction(() => {
      this.isLoading = true;
      this.errorMessage = null;
    });

    try {
      const response = await this.campaignAPI.getCampaignListing({
        page_no: this.pageNo,
        limit: this.limit,
        campaign_id: campaignId,
        search: searchText,
        status,
      });

      const result = (response?.result || {}) as Partial<
        PaginatedAPIResponse<CampaignListItem>
      >;
      const list = Array.isArray(result.list) ? result.list : [];

      runInAction(() => {
        this.rows = list;
        this.totalCount = Number(result.total_count || 0);
      });
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.errorMessage =
          error?.response?.data?.message || "Failed to fetch campaigns";
        this.rows = [];
        this.totalCount = 0;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  getCampaignEligibleEmployees = async (campaignId: number, dateUtc: string) => {
    runInAction(() => {
      this.isEligibleEmployeesLoading = true;
      this.eligibleEmployeesErrorMessage = null;
    });

    try {
      const response = await this.campaignAPI.getCampaignEligibleEmployees(campaignId, {
        date_utc: dateUtc,
        search: this.eligibleEmployeesAppliedSearch,
      });
      const result = (response?.result || {}) as Partial<CampaignEligibleEmployeesResponse>;
      const list = Array.isArray(result.list) ? result.list : [];

      runInAction(() => {
        this.eligibleEmployees = list as CampaignEligibleEmployeeRow[];
        this.campaignDetailsCustomerId =
          result.customer_id === null || result.customer_id === undefined
            ? null
            : Number(result.customer_id);
      });

      return response;
    } catch (error: any) {
      runInAction(() => {
        this.eligibleEmployeesErrorMessage =
          error?.response?.data?.message || "Failed to fetch eligible employees";
        this.eligibleEmployees = [];
        this.campaignDetailsCustomerId = null;
      });
    } finally {
      runInAction(() => {
        this.isEligibleEmployeesLoading = false;
      });
    }
  };

  getCampaignTodayShipments = async (campaignId: number, dateUtc: string) => {
    runInAction(() => {
      this.isTodayShipmentsLoading = true;
      this.todayShipmentsErrorMessage = null;
    });

    try {
      const response = await this.campaignAPI.getCampaignTodayShipments(campaignId, {
        date_utc: dateUtc,
        page_no: this.todayShipmentsPageNo,
        limit: this.todayShipmentsLimit,
        status: 1,
        sort_by: "created_date",
        sort_type: "DESC",
        search: this.todayShipmentsAppliedSearch || undefined,
      });

      const result = (response?.result || {}) as Partial<
        PaginatedAPIResponse<ShipmentsDetails>
      >;
      const list = Array.isArray(result.list) ? result.list : [];

      runInAction(() => {
        this.todayShipments = list as ShipmentsDetails[];
        this.todayShipmentsTotalCount = Number(result.total_count || 0);
        this.todayShipmentsTotalPages = Number(result.total_pages || 0);
      });

      return response;
    } catch (error: any) {
      runInAction(() => {
        this.todayShipmentsErrorMessage =
          error?.response?.data?.message || "Failed to fetch today shipments";
        this.todayShipments = [];
        this.todayShipmentsTotalCount = 0;
        this.todayShipmentsTotalPages = 0;
      });
    } finally {
      runInAction(() => {
        this.isTodayShipmentsLoading = false;
      });
    }
  };

  clearCampaignDetailsState = () => {
    this.campaignDetailsTab = "eligible_employees";

    this.eligibleEmployees = [];
    this.isEligibleEmployeesLoading = false;
    this.eligibleEmployeesErrorMessage = null;
    this.eligibleEmployeesSearchInput = "";
    this.eligibleEmployeesAppliedSearch = "";
    this.campaignDetailsCustomerId = null;

    this.todayShipments = [];
    this.isTodayShipmentsLoading = false;
    this.todayShipmentsErrorMessage = null;
    this.todayShipmentsSearchInput = "";
    this.todayShipmentsAppliedSearch = "";
    this.todayShipmentsPageNo = 1;
    this.todayShipmentsLimit = 30;
    this.todayShipmentsTotalCount = 0;
    this.todayShipmentsTotalPages = 0;
  };

  reset = () => {
    this.rows = [];
    this.isLoading = false;
    this.errorMessage = null;
    this.pageNo = 1;
    this.limit = 10;
    this.totalCount = 0;
    this.isFiltersExpanded = true;
    this.statusTab = "active";
    this.searchInput = "";
    this.appliedSearch = "";
    this.clearCampaignDetailsState();
  };
}
